import type { IRewardsService } from '../../services/rewards/IRewardsService';
import type { SkuDetail, ReserveResult, RedeemResult } from '../../services/rewards/types';
import { serviceContainer } from '../../services/ServiceContainer';

export type RedeemPhase = 'detail' | 'confirm' | 'confirming' | 'success' | 'oos' | 'failure' | 'expired';

const RESERVATION_TTL_S = 30;
const RESERVATION_BUFFER_S = 2;

export interface RedeemControllerDeps {
  skuId: string;
  rewardsService: IRewardsService;
  onComplete: () => void;
}

export class RedeemController {
  private phase: RedeemPhase = 'detail';
  private skuDetail: SkuDetail | null = null;
  private reserveResult: ReserveResult | null = null;
  private redeemResult: RedeemResult | null = null;
  private error: string | null = null;
  private loading = true;
  private reserving = false;
  private secondsLeft = 30;
  private clientRequestId = crypto.randomUUID();
  private claimId: string | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private listeners: Array<() => void> = [];
  private destroyed = false;
  private reserveGeneration = 0;

  private readonly skuId: string;
  private readonly rewardsService: IRewardsService;
  private readonly onComplete: () => void;

  constructor(deps: RedeemControllerDeps) {
    this.skuId = deps.skuId;
    this.rewardsService = deps.rewardsService;
    this.onComplete = deps.onComplete;
  }

  /** Call after subscribe() is attached to guarantee the listener receives the result. */
  start(): void {
    this.fetchDetail();
  }

  getPhase(): RedeemPhase { return this.phase; }
  getSkuDetail(): SkuDetail | null { return this.skuDetail; }
  getRedeemResult(): RedeemResult | null { return this.redeemResult; }
  getError(): string | null { return this.error; }
  isLoading(): boolean { return this.loading; }
  isReserving(): boolean { return this.reserving; }
  getSecondsLeft(): number { return this.secondsLeft; }

  subscribe(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      const idx = this.listeners.indexOf(listener);
      if (idx > -1) this.listeners.splice(idx, 1);
    };
  }

  async startReserve(): Promise<void> {
    if (this.reserving) return;

    const gen = ++this.reserveGeneration;
    this.phase = 'confirm';
    this.reserving = true;
    this.error = null;
    this.notify();

    try {
      const res = await this.rewardsService.reserve(this.skuId, this.clientRequestId);
      if (this.destroyed || gen !== this.reserveGeneration) {
        if (res.claimId) {
          this.rewardsService.cancel(res.claimId).catch(() => {});
        }
        return;
      }

      if (res.error) {
        this.reserving = false;
        if (res.error === 'OUT_OF_STOCK') {
          this.phase = 'oos';
        } else {
          this.error = res.error;
          this.phase = 'failure';
        }
        this.notify();
        return;
      }

      if (!res.claimId) {
        this.reserving = false;
        this.error = 'Failed to reserve — please try again.';
        this.phase = 'failure';
        this.notify();
        return;
      }

      this.reserveResult = res;
      this.claimId = res.claimId;
      this.reserving = false;
      this.startCountdown();
      this.notify();
    } catch (err: any) {
      if (this.destroyed || gen !== this.reserveGeneration) return;
      this.reserving = false;
      this.error = err?.message ?? 'Failed to reserve';
      this.phase = 'failure';
      this.notify();
    }
  }

  private static readonly TERMINAL_CONFIRM_ERRORS = new Set([
    'CLAIM_NOT_FOUND', 'INVALID_STATE', 'SKU_NOT_FOUND', 'RESERVATION_EXPIRED',
  ]);

  async confirmRedemption(): Promise<void> {
    if (!this.claimId || this.phase === 'confirming') return;
    this.clearTimer();
    this.phase = 'confirming';
    this.notify();

    try {
      const res = await this.rewardsService.confirm(this.claimId);
      if (this.destroyed) return;

      if (res.success && !res.error) {
        this.redeemResult = res;
        this.phase = 'success';
        this.onComplete();
        this.sendRedeemAnalytics();
      } else {
        this.error = res.error ?? 'Redemption failed unexpectedly';
        this.phase = 'failure';
        if (RedeemController.TERMINAL_CONFIRM_ERRORS.has(res.error ?? '')) {
          this.claimId = null;
        }
        this.sendFailureAnalytics(res.error ?? 'unknown');
      }
      this.notify();
    } catch {
      if (this.destroyed) return;
      this.error = 'Network error. Please try again.';
      this.phase = 'failure';
      this.sendFailureAnalytics('Network error');
      this.notify();
    }
  }

  async retryAfterNetworkError(): Promise<void> {
    if (this.phase === 'confirming') return;
    if (!this.claimId) {
      this.retryReserve();
      return;
    }
    this.phase = 'confirming';
    this.notify();
    try {
      const res = await this.rewardsService.confirm(this.claimId);
      if (this.destroyed) return;
      if (res.success && !res.error) {
        this.redeemResult = res;
        this.phase = 'success';
        this.onComplete();
        this.sendRedeemAnalytics();
      } else if (RedeemController.TERMINAL_CONFIRM_ERRORS.has(res.error ?? '')) {
        this.claimId = null;
        this.retryReserve();
        return;
      } else {
        this.error = res.error ?? 'Redemption failed';
        this.phase = 'failure';
        this.sendFailureAnalytics(res.error ?? 'unknown');
      }
      this.notify();
    } catch {
      if (this.destroyed) return;
      this.error = 'Network error. Please try again.';
      this.phase = 'failure';
      this.sendFailureAnalytics('Network error');
      this.notify();
    }
  }

  retryReserve(): void {
    this.claimId = null;
    this.clientRequestId = crypto.randomUUID();
    this.startReserve();
  }

  async cancel(): Promise<void> {
    this.clearTimer();
    this.reserveGeneration++;
    this.reserving = false;
    if (this.claimId) {
      this.rewardsService.cancel(this.claimId).catch(() => {});
    }
    this.claimId = null;
    this.clientRequestId = crypto.randomUUID();
    this.phase = 'detail';
    this.notify();
  }

  async retryFetchDetail(): Promise<void> {
    await this.fetchDetail();
  }

  destroy(): void {
    this.destroyed = true;
    this.clearTimer();
    if (this.phase === 'confirm' && this.claimId && !this.reserving) {
      this.rewardsService.cancel(this.claimId).catch(() => {});
    }
    this.claimId = null;
    this.listeners = [];
  }

  private async fetchDetail(): Promise<void> {
    this.loading = true;
    this.notify();
    try {
      const data = await this.rewardsService.getSkuDetail(this.skuId);
      if (this.destroyed) return;
      if (data && !('success' in data && !(data as any).success)) {
        this.skuDetail = data;
        this.error = null;
      } else {
        this.error = (data as any)?.error ?? 'Item not found';
      }
    } catch (err) {
      if (this.destroyed) return;
      this.error = err instanceof Error ? err.message : 'Failed to load item';
    } finally {
      if (!this.destroyed) {
        this.loading = false;
        this.notify();
      }
    }
  }

  private startCountdown(): void {
    this.clearTimer();
    this.secondsLeft = RESERVATION_TTL_S - RESERVATION_BUFFER_S;
    this.timer = setInterval(() => {
      this.secondsLeft -= 1;
      if (this.secondsLeft <= 0) {
        this.clearTimer();
        if (this.claimId) {
          this.rewardsService.cancel(this.claimId).catch(() => {});
        }
        this.claimId = null;
        this.clientRequestId = crypto.randomUUID();
        this.phase = 'expired';
      }
      this.notify();
    }, 1000);
  }

  private clearTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private sendRedeemAnalytics(): void {
    if (!this.skuDetail) return;
    serviceContainer.analyticsManager.sendRewardRedeemedEvent({
      skuId: this.skuId,
      skuName: this.skuDetail.name,
      pointCost: this.skuDetail.pointCost,
      monetaryValue: this.skuDetail.monetaryValue,
    });
  }

  private sendFailureAnalytics(reason: string): void {
    serviceContainer.analyticsManager.sendRedeemAttemptFailedEvent({
      skuId: this.skuId,
      reason,
    });
  }
}
